import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';

// Labels after the area in the top bar's breadcrumb: [what the area picked (assistant, portal)], then the page.
// An item is a string, or `{ label, to }` for a level that links back (see `subPage`).
// The page is the active tier-2 item (same i18n keys as the menu). A page with no menu item falls back to the
// title its route gives the page header (`headerTitle`, an i18n key); with none, the trail is empty and the bar
// shows the area itself, so the breadcrumb is never blank.
export function useTopbarTrail(activeArea, isItemActive) {
  const { t, te } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const assistants = useMapGetter('polarisAssistants/getRecords');
  const portals = useMapGetter('portals/allPortals');

  const activeItem = computed(() =>
    (activeArea.value?.items ?? [])
      .flatMap(item => item.children || [item])
      .find(item => item.to && isItemActive(item))
  );

  // Pages with no menu item and no header title of their own, named with the keys the app already has.
  const namedRoutes = computed(() => ({
    search: t('SIDEBAR.SEARCH'),
    portals_new: t('HELP_CENTER.PORTAL_SWITCHER.NEW_PORTAL'),
  }));

  const routeTitle = computed(() => {
    // route props are an object or a function of the route; some carry the page header's i18n key
    const keys = [...route.matched].reverse().map(record => {
      const props = record.props?.default;
      return (typeof props === 'function' ? props(route) : props)?.headerTitle;
    });
    const key = keys.find(item => typeof item === 'string');
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    if (key && te(key)) return t(key);
    return namedRoutes.value[route.name] ?? '';
  });

  // What the area picked, shown between the area and the page (the tier-2 picker's own lookup).
  const picked = computed(() => {
    if (activeArea.value?.name === 'polaris') {
      const id = Number(route.params.assistantId) || null;
      return assistants.value?.find(item => item.id === id)?.name;
    }
    if (activeArea.value?.name === 'knowledge') {
      return portals.value?.find(item => item.slug === route.params.portalSlug)
        ?.name;
    }
    return undefined;
  });

  // A page one level under a menu item (Polaris AI › Settings › Guardrails): its route declares the item's route
  // (`meta.trailParent`) and its own label (`meta.trailLabel`, an i18n key). The item becomes a link in the middle
  // of the trail, the way back now that the page has no "Back" button (phase 4.1, D-G2).
  // A detail page names itself from its record instead (Reports › Agents › John): `meta.trailDetail(route, store)`
  // returns the name, read from the store like `picked` does; it needs `trailParent`, not `trailLabel`. Until the
  // record is in the store it returns nothing, and the page shows as the menu item alone.
  const subPage = computed(() => {
    const { trailParent, trailLabel, trailDetail } = route.meta ?? {};
    if (!trailParent) return null;
    const detail =
      typeof trailDetail === 'function' ? trailDetail(route, store) : '';
    if (detail) return { parent: trailParent, label: detail };
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    if (!trailLabel || !te(trailLabel)) return null;
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return { parent: trailParent, label: t(trailLabel) };
  });

  // The link back takes only the params its own route has (a detail's `:id` is not a list's param).
  const parentParams = name => {
    const record = router.getRoutes().find(item => item.name === name);
    if (!record) return route.params;
    const keys = [...record.path.matchAll(/:(\w+)/g)].map(match => match[1]);
    return Object.fromEntries(
      Object.entries(route.params).filter(([key]) => keys.includes(key))
    );
  };

  // A route may name its own page over the menu item that lights up for it (`meta.trailLabel` without a parent:
  // "Assistants" when there is no assistant yet, while tier 2 lights Overview).
  const ownLabel = computed(() => {
    const { trailParent, trailLabel } = route.meta ?? {};
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return !trailParent && trailLabel && te(trailLabel) ? t(trailLabel) : '';
  });

  const trail = computed(() => {
    const page = ownLabel.value || activeItem.value?.label || routeTitle.value;
    if (!page) return [];
    if (subPage.value) {
      const to = {
        name: subPage.value.parent,
        params: parentParams(subPage.value.parent),
      };
      return [picked.value, { label: page, to }, subPage.value.label].filter(
        Boolean
      );
    }
    return [picked.value, page].filter(Boolean);
  });

  return { trail };
}
